import React from 'react';
import { Flex, Text, Tooltip } from '@chakra-ui/react';
import { Warning, WarningCircle } from 'phosphor-react';
import { useWatch } from 'react-hook-form';
import { validarCpf } from '../../../../../utils/validarCpf';

interface CampoExtraidoWarningProps {
  /** Path do field no react-hook-form (ex: `socios.0.cpf`). */
  fieldName: string;
  /** Se a IA tentou preencher esse campo nesse socio. */
  extracaoFeita: boolean;
  /**
   * Quando `true`, ativa validacao modulo 11 do CPF — exibe warning
   * vermelho quando preenchido mas falha DV. Default `false`.
   */
  validarComoCpf?: boolean;
  /**
   * Nome user-facing usado no tooltip do warning (ex: "CPF", "RG").
   */
  campoLabel: string;
}

// Warning sinalizador SO em casos problematicos (decisao UX 2026-05-19:
// sem sinal verde — operador SEMPRE confere visualmente o documento).
// Aparece em 2 situacoes:
//
// 1. Campo vazio apos extracao: amarelo, "IA nao conseguiu extrair — preencha
//    manualmente". Some automatico quando operador digita algo.
//
// 2. CPF preenchido mas falha modulo 11: vermelho, "CPF parece invalido —
//    confira no documento". Algoritmo deterministico, sem falso positivo.
//
// 3. Nenhuma das anteriores: nao renderiza nada (silencioso e bom — sem
//    incentivar operador a confiar que "ta tudo OK").
export const CampoExtraidoWarning = ({
  fieldName,
  extracaoFeita,
  validarComoCpf = false,
  campoLabel,
}: CampoExtraidoWarningProps) => {
  const valor = useWatch({ name: fieldName }) as string | undefined | null;

  if (!extracaoFeita) return null;

  const vazio = !valor;
  const cpfInvalido = validarComoCpf && !!valor && !validarCpf(valor);

  if (vazio) {
    return (
      <Tooltip
        label="A IA nao conseguiu extrair este campo. Confira o documento e preencha manualmente."
        hasArrow
        placement="bottom-start"
      >
        <Flex align="center" gap={1} color="orange.500" fontSize="xs" mt={1}>
          <Warning size={14} weight="bold" />
          <Text>IA nao extraiu — preencha manualmente</Text>
        </Flex>
      </Tooltip>
    );
  }

  if (cpfInvalido) {
    return (
      <Tooltip
        label={`${campoLabel} parece invalido. Confira no documento — IA pode ter confundido digitos similares (7/2, 0/8, 1/7).`}
        hasArrow
        placement="bottom-start"
      >
        <Flex align="center" gap={1} color="red.500" fontSize="xs" mt={1}>
          <WarningCircle size={14} weight="bold" />
          <Text>{campoLabel} parece incorreto — confira no documento</Text>
        </Flex>
      </Tooltip>
    );
  }

  return null;
};

export default CampoExtraidoWarning;
